import React from 'react';
import {
  Badge,
  Box,
  ButtonIcon,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';

import { CF_PRI_MED } from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { FlagPopover } from '../FlagPopover';

import type { TimelineItemActionsProps } from './types';

import './TimelineItem.css';

/**
 * TimelineItemActions Component
 * Renders the actions column with flag and more buttons
 */
function TimelineItemActions({
  handleFlagSave,
  handleCommentSave,
  handleFlagResolve,
  isFlagPopoverOpen,
  setIsFlagPopoverOpen,
  flagData,
  onCommentClick,
  onResolveFlagClick,
  onViewActivities,
  commentsCount,
  rowLogStatus,
}: TimelineItemActionsProps): React.ReactElement {
  const l = useInvertedLocalize();
  const flagStatus = flagData?.status ?? 'none';
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);
  const isReadOnly = Boolean(
    useTypedSelector((s) => s.aag.auditLog.isReadOnly)
  );
  const [isCommentOnly, setIsCommentOnly] = React.useState(false);
  const [initialResolveOpen, setInitialResolveOpen] = React.useState(false);
  // Normalize commentsCount in case API sends string
  const badgeCount = React.useMemo(() => {
    const n = Number(commentsCount ?? 0);

    return Number.isFinite(n) ? n : 0;
  }, [commentsCount]);

  // Determine flag button styling based on status
  const getFlagButtonStyle = () => {
    switch (flagStatus) {
      case 'needs_resolution':
        return {
          backgroundColor: 'var(--color-background-warning-medium, #f6a91c)',
          borderColor: 'var(--color-border-warning-medium, #f6a91c)',
          iconColor: 'var(--color-foreground-neutral-inverse, #ffffff)',
        } as const;
      case 'resolved':
        return {
          backgroundColor: 'var(--color-background-success-medium, #22c55e)',
          borderColor: 'var(--color-border-success-medium, #22c55e)',
          iconColor: 'var(--color-foreground-neutral-inverse, #ffffff)',
        } as const;
      default:
        return {
          backgroundColor: 'transparent',
          borderColor: 'var(--color-border-primary-medium, #6e7fff)',
          iconColor: CF_PRI_MED,
        } as const;
    }
  };

  const flagButtonStyle = getFlagButtonStyle();
  const flagIconKind = React.useMemo(() => {
    if (flagStatus === 'none') {
      return 'Flag';
    }

    if (flagStatus === 'resolved') {
      return 'FlagResolved';
    }

    return 'FlagFilled';
  }, [flagStatus]);

  // Reset internal modes when status is no longer FLAGGED
  React.useEffect(() => {
    if (rowLogStatus !== 'FLAGGED') {
      setInitialResolveOpen(false);
      setIsCommentOnly(false);
    }
  }, [rowLogStatus]);

  // Handle comment menu item click (open popover on the action button)
  const handleCommentMenuClick = () => {
    setIsMoreOpen(false);

    if (onCommentClick) {
      setIsCommentOnly(true);
      onCommentClick();
    }
  };

  // Handle resolve flag from more menu (opens flag popover to resolve)
  const handleResolveMenuClick = () => {
    setIsMoreOpen(false);

    if (onResolveFlagClick) {
      setInitialResolveOpen(true);
      onResolveFlagClick();
    }
  };

  // Handle view activities (open detail panel)
  const handleViewActivitiesClick = () => {
    setIsMoreOpen(false);

    if (onViewActivities) {
      onViewActivities('review-activity');
    }
  };

  // Handle view all activities for flag popover
  const handleViewAllActivities = React.useCallback(() => {
    if (onViewActivities) {
      onViewActivities('review-activity');
    }
  }, [onViewActivities]);

  // Create the menu for the More button
  const getMoreButtonMenu = () => {
    const viewCommentsLabel = l('View Comments');
    const menuItems = [];

    // Add "View Comments" first if status is COMMENTED
    if (rowLogStatus === 'COMMENTED') {
      menuItems.push(
        <div
          key='view-comments'
          className='Aag-TimelineItem-viewCommentsMenuItem'
          role='button'
          tabIndex={0}
          onClick={handleViewActivitiesClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleViewActivitiesClick();
            }
          }}>
          <Box className='Aag-TimelineItem-viewCommentsMenuItemContent'>
            <Icon kind='ArrowRight' size='small' />
            <Typography kind='body2'>{viewCommentsLabel}</Typography>
          </Box>
          {badgeCount > 0 && (
            <Badge
              kind='number'
              color='primary'
              number={badgeCount}
              size='small'
            />
          )}
        </div>
      );
    }

    // Add "Comment" second
    menuItems.push(
      <MenuItem
        key='comment'
        option={{ label: l('Comment'), value: 'comment' }}
        onClick={handleCommentMenuClick}
        prefixIcon={<Icon kind='Comment' size='small' />}
      />
    );

    // Add other conditional items
    if (flagStatus === 'resolved') {
      menuItems.push(
        <MenuItem
          key='view-activities'
          option={{ label: l('View All Activities'), value: 'view-activities' }}
          onClick={handleViewActivitiesClick}
          prefixIcon={<Icon kind='ArrowRight' size='small' />}
        />
      );
    } else if (flagStatus === 'needs_resolution') {
      menuItems.push(
        <MenuItem
          key='resolve-flag'
          option={{ label: l('Resolve Flag'), value: 'resolve-flag' }}
          onClick={handleResolveMenuClick}
          prefixIcon={<Icon kind='FlagResolved' size='small' />}
        />
      );
    }

    return <Box className='Aag-TimelineItem-moreMenu'>{menuItems}</Box>;
  };

  // Hide flag button in readonly mode when status is FLAGGED or PENDING_REVIEW
  const shouldHideFlagButton: boolean =
    isReadOnly &&
    (rowLogStatus === 'FLAGGED' || rowLogStatus === 'PENDING_REVIEW');

  // Properly typed variable for onViewAllActivities prop
  const viewAllActivitiesHandler: (() => void) | undefined =
    typeof onViewActivities === 'function'
      ? handleViewAllActivities
      : undefined;

  return (
    <Box className='Aag-TimelineItem-actions'>
      {!shouldHideFlagButton && (
        <Box className='Aag-TimelineItem-actions-flag-button'>
          <FlagPopover
            trigger={
              <div
                className='Aag-TimelineItem-flagButtonTrigger'
                data-flag-status={flagStatus}
                onClick={() => setIsCommentOnly(false)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsCommentOnly(false);
                    setInitialResolveOpen(false);
                  }
                }}>
                <ButtonIcon
                  kind='ghost'
                  size='large'
                  ariaLabel={l('close')}
                  icon={
                    <Icon
                      kind={flagIconKind}
                      color={flagButtonStyle.iconColor}
                    />
                  }
                />
              </div>
            }
            onSave={(c) => {
              // Treat both explicit comment-only mode and FLAGGED (needs_resolution) comment form as comments
              if (isCommentOnly || flagStatus === 'needs_resolution') {
                return (handleCommentSave ?? handleFlagSave)(c);
              }

              if (flagStatus === 'resolved' && handleCommentSave) {
                return handleCommentSave(c);
              }

              return handleFlagSave(c);
            }}
            onResolve={handleFlagResolve}
            isOpen={isFlagPopoverOpen}
            setIsOpen={(open) => {
              if (!open) {
                setIsCommentOnly(false);
                setInitialResolveOpen(false);
              }

              setIsFlagPopoverOpen(open);
            }}
            flagData={flagData}
            onViewAllActivities={viewAllActivitiesHandler}
            initialResolve={initialResolveOpen}
            commentOnly={isCommentOnly}
          />
        </Box>
      )}
      <Menu
        CustomMenu={getMoreButtonMenu()}
        isPortalEnabled
        isMenuExpanded={isMoreOpen}
        setIsExpanded={setIsMoreOpen}
        trigger={
          <div className='Aag-TimelineItem-moreButton-container'>
            <ButtonIcon
              kind='ghost'
              size='medium'
              ariaLabel={l('more')}
              icon={<Icon kind='MoreVertical' />}
              className='Aag-TimelineItem-moreButton'
            />
            {badgeCount > 0 && !isReadOnly && (
              <Badge
                className='Aag-TimelineItem-moreButton-transactionActions-Badge'
                kind='number'
                number={badgeCount}
                size='small'
              />
            )}
          </div>
        }
        className='Aag-TimelineItem-moreMenu-container'
      />
    </Box>
  );
}

TimelineItemActions.defaultProps = {
  handleCommentSave: undefined,
  handleFlagResolve: undefined,
  flagData: undefined,
  onCommentClick: undefined,
  onResolveFlagClick: undefined,
  onViewActivities: undefined,
  commentsCount: undefined,
  rowLogStatus: undefined,
};

export default TimelineItemActions;
